// docs https://github.com/azouaoui-med/react-pro-sidebar
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Menu, Sidebar, MenuItem } from "react-pro-sidebar";
import { useProSidebar } from "react-pro-sidebar";

import { useSidebarContext } from "./sidebarContext";
import InventoryIcon from '@mui/icons-material/Inventory';
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../../theme";
import { useTheme, Box, Typography, IconButton } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import BusinessIcon from '@mui/icons-material/Business';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SwitchRightOutlinedIcon from "@mui/icons-material/SwitchRightOutlined";
import SwitchLeftOutlinedIcon from "@mui/icons-material/SwitchLeftOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { useAuth } from "../../../context/AuthContext";
import { menuGroups, selectedKey } from "./menu";
import { useAdminLook } from "../../../shared/design/lookContext";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import InitialsAvatar from "../../../components/InitialsAvatar";
type MenuItemProps = {
  title: string;
  to: string;
  icon: ReactNode;
  isActive: boolean;
  setSelected: (value: string) => void;
};

const Item = ({ title, to, icon, isActive, setSelected }: MenuItemProps) => {
  // The actual styling (active pill, hover, color) lives on the parent
  // `Menu`'s `menuItemStyles` config — react-pro-sidebar applies it to
  // every MenuItem so the look is consistent across the sidebar and we
  // don't repeat per-item style logic here.
  return (
    <MenuItem
      active={isActive}
      onClick={() => setSelected(title)}
      icon={icon}
      routerLink={<Link to={to} />}
    >
      {/* Long labels wrap instead of being cut («Өрөө ба тоног төхөөрөмж»). */}
      <Typography sx={{ fontWeight: isActive ? 600 : 400, whiteSpace: "normal", lineHeight: 1.25 }}>{title}</Typography>
    </MenuItem>
  );
};

const ICONS: Record<string, ReactNode> = {
  frontDesk: <RoomServiceOutlinedIcon />,
  bookings: <ListAltOutlinedIcon />,
  service: <HomeRepairServiceIcon />,
  staff: <PeopleOutlinedIcon />,
  team: <InventoryIcon />,
  branch: <BusinessIcon />,
  hours: <ScheduleOutlinedIcon />,
  limits: <PrecisionManufacturingOutlinedIcon />,
  category: <ReceiptOutlinedIcon />,
  report: <HomeOutlinedIcon />,
  settings: <BusinessCenterOutlinedIcon />,
  members: <GroupAddOutlinedIcon />,
  switch: <BusinessCenterOutlinedIcon />,
  users: <PeopleOutlinedIcon />,
  scheduleAudit: <HistoryOutlinedIcon />,
  serviceAudit: <HistoryOutlinedIcon />,
  look: <PaletteOutlinedIcon />,
};

const MyProSidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const [, setSelected] = useState("");
  const { sidebarRTL, setSidebarRTL, sidebarImage } = useSidebarContext();
  const { collapseSidebar, toggleSidebar, collapsed, broken } = useProSidebar();

  const { userInfo, hasBookingAdmin } = useAuth();
  const user = userInfo();

  // The highlighted item follows the route (C4 §4: `menu.ts` maps every
  // routed page to its item; pages outside the menu highlight nothing).
  const activeKey = selectedKey(location.pathname);
  const { settingAvailable } = useAdminLook();
  const groups = useMemo(
    () => menuGroups(hasBookingAdmin(), { lookSetting: settingAvailable }),
    [hasBookingAdmin, settingAvailable],
  );
  // Chrome bg uses the deep token (primary[500]) in dark mode and the
  // light token (primary[400]) in light mode. Item-level styles live on
  // the wrapper Box's sx (cascading selectors) — this v0.x lib applies
  // its bg on `.menu-anchor`, not the outer wrapper.
  const isDark = theme.palette.mode === "dark";
  const sidebarBg = colors.primary[isDark ? 500 : 400];

  // react-pro-sidebar v0.x renders item bg on the inner `.menu-anchor`
  // <a>, not the outer `.menu-item` <li>. Wrapper-level sx with cascading
  // selectors is the only reliable way to override (the per-item
  // renderMenuItemStyles config in this version targets the wrong node).
  const inactiveText = isDark ? colors.grey[200] : colors.grey[300];
  const hoverBg = isDark
    ? "rgba(0, 117, 255, 0.12)"
    : "rgba(0, 117, 255, 0.08)";

  return (
    <Box
      sx={{
        position: "sticky",
        display: "flex",
        height: "100vh",
        top: 0,
        bottom: 0,
        zIndex: 10000,
        // Sidebar own scroll — `100vh` with long item list overflows the
        // body; let the inner Sidebar scroll instead of the page.
        // Custom thin scrollbar so it doesn't eat horizontal space the
        // active pill needs (right-edge bleed bug).
        "& .sidebar, & .ps-sidebar-container": {
          border: "none",
          backgroundColor: `${sidebarBg} !important`,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${
            isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)"
          } transparent`,
        },
        "& .ps-sidebar-container::-webkit-scrollbar": { width: 6 },
        "& .ps-sidebar-container::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "& .ps-sidebar-container::-webkit-scrollbar-thumb": {
          background: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
          borderRadius: 4,
        },
        // Item shell: rounded pill that hugs the anchor. Slightly tighter
        // right margin (10px vs 12px) to keep the pill clear of the 6px
        // scrollbar and any rounding artefacts.
        "& .menu-item": {
          margin: "2px 10px 2px 12px !important",
          borderRadius: "8px !important",
          overflow: "hidden",
          backgroundColor: "transparent !important",
        },
        // Default anchor: muted text, transparent bg.
        "& .menu-anchor": {
          color: `${inactiveText} !important`,
          backgroundColor: "transparent !important",
          borderRadius: "8px !important",
          transition: "background-color 120ms, color 120ms",
          // Dense rows (44px) so the whole grouped menu fits (C4 review).
          minHeight: "44px !important",
          height: "auto !important",
          paddingTop: "4px !important",
          paddingBottom: "4px !important",
        },
        // Hover (non-active): soft blue tint + brand-blue text.
        "& .menu-item:not(.active) .menu-anchor:hover": {
          backgroundColor: `${hoverBg} !important`,
          color: `${colors.blueAccent[isDark ? 400 : 600]} !important`,
        },
        // Active: brand blue filled pill, white text+icon.
        "& .menu-item.active .menu-anchor": {
          backgroundColor: `${colors.blueAccent[500]} !important`,
          color: "#fff !important",
        },
        "& .menu-item.active .menu-anchor:hover": {
          backgroundColor: `${colors.blueAccent[600]} !important`,
        },
        "& .menu-icon": {
          color: "inherit !important",
          backgroundColor: "transparent !important",
        },
      }}
    >
      <Sidebar
        customBreakPoint="900px"
        rtl={sidebarRTL}
        backgroundColor={sidebarBg}
        image={sidebarImage}
      >
        <Menu>
          {/* Top toggle row — collapse/expand control. Plain MenuItem
              renders a single-line bar; the previous version stuffed a
              redundant "User" h3 (24px) inside which competed with the
              actual username below. Dropped. */}
          <MenuItem
            icon={
              collapsed ? (
                <MenuOutlinedIcon onClick={() => collapseSidebar()} />
              ) : (<></>)
              // sidebarRTL ? (
              //   <SwitchLeftOutlinedIcon
              //     onClick={() => setSidebarRTL(!sidebarRTL)}
              //   />
              // ) : (
              //   <SwitchRightOutlinedIcon
              //     onClick={() => setSidebarRTL(!sidebarRTL)}
              //   />
              // )
            }
            style={{
              margin: "10px 0 16px 0",
              color: colors.grey[200],
            }}
          >
            {!collapsed && (
              <Box display="flex" justifyContent="flex-end" alignItems="center" pr={1}>
                <IconButton
                  size="small"
                  onClick={
                    broken ? () => toggleSidebar() : () => collapseSidebar()
                  }
                  sx={{ color: colors.grey[200] }}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </MenuItem>
          {/* User identity — avatar 56px (was 88, way oversized for a
              sidebar). Username uses subtitle1 (16px) instead of h3 (24px).
              Email caption stays muted. Compact spacing keeps the nav
              items above the fold on shorter viewports. */}
          {!collapsed && (
            <Box mb={2.5}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <InitialsAvatar name={user.name} email={user.email} size={40} />
              </Box>
              <Box textAlign="center" px={1.5}>
                <Typography
                  variant="subtitle1"
                  color={colors.grey[100]}
                  fontWeight={600}
                  sx={{ mt: 1, lineHeight: 1.2 }}
                  noWrap
                >
                  {user.name}
                </Typography>
                <Typography
                  variant="caption"
                  color={colors.grey[300]}
                  noWrap
                  sx={{ display: "block" }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>
          )}
          <Box>
            {groups.map((g) => (
              <Box key={g.label}>
                {!collapsed && (
                  <Typography
                    variant="overline"
                    color={colors.grey[400]}
                    sx={{
                      display: "block",
                      mt: 2,
                      mb: 0.5,
                      mx: 2.25, // aligns with the item label after the icon column
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 1.2,
                      lineHeight: 1.4,
                    }}
                  >
                    {g.label}
                  </Typography>
                )}
                {g.items.map((i) => (
                  <Item
                    key={i.key}
                    title={i.label}
                    to={i.to}
                    icon={ICONS[i.key] ?? <HubOutlinedIcon />}
                    isActive={activeKey === i.key}
                    setSelected={(v) => {
                      setSelected(v);
                      // On a phone the menu is a drawer: close it once a page is picked.
                      if (broken) toggleSidebar();
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Menu>
      </Sidebar>
    </Box>
  );
};

export default MyProSidebar;
